// src/components/pdf-viewer/PDFViewerModal.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Spin, Typography, message } from 'antd';
import { getAuth } from 'firebase/auth';
import { PDFViewer } from './PDFViewer';
import type { ChatMessage } from './ReportAssistant';
import type { NarrationSection } from './NarrationPlayer';
import { usePDFAnnotations } from '../../hooks/usePDFAnnotations';
import type { CreateAnnotationRequest } from '../../types/annotation';

interface PDFViewerModalProps {
  visible: boolean;
  onClose: () => void;
  pdfUrl: string;
  documentId: string; // Usually the job_id
  documentTitle?: string; // For display in modal title
}

export const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  visible,
  onClose,
  pdfUrl,
  documentId,
  documentTitle,
}) => {
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [sections, setSections] = useState<NarrationSection[] | undefined>(undefined);
  // Modal goes full-screen on phones (where every pixel matters) but stays
  // a windowed dialog on desktop so the sidebar can sit beside the report.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // workOrderLoading no longer needed - sidebar modal handles its own loading
  const {
    annotations,
    loading: annotationsLoading,
    fetchAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = usePDFAnnotations();

  // Fetch annotations when modal becomes visible
  useEffect(() => {
    if (visible && documentId) {
      console.log(`[PDFViewerModal] Modal opened, fetching annotations for document: ${documentId}`);
      fetchAnnotations(documentId);
    }
  }, [visible, documentId, fetchAnnotations]);

  // Hide the global mobile hamburger menu while the full-screen viewer is open
  useEffect(() => {
    if (!visible) return;
    document.body.classList.add('pdf-viewer-open');
    return () => {
      document.body.classList.remove('pdf-viewer-open');
    };
  }, [visible]);

  // Fetch narration sections (best-effort - if it fails, the player just doesn't render)
  useEffect(() => {
    let cancelled = false;
    if (!visible || !documentId) {
      setSections(undefined);
      return;
    }
    (async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const response = await fetch(`${API_URL}/jobs/${documentId}/narration-sections`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          // Non-fatal: many PDFs (e.g. work-order docs) won't have narration
          if (!cancelled) setSections(undefined);
          return;
        }
        const data = await response.json();
        const fetched: NarrationSection[] = data?.sections || [];
        if (!cancelled) setSections(fetched.length > 0 ? fetched : undefined);
      } catch (err) {
        console.warn('[PDFViewerModal] Could not fetch narration sections:', err);
        if (!cancelled) setSections(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, documentId]);

  // Handle annotation creation - wrapper to add documentId
  const handleAnnotationCreate = async (data: CreateAnnotationRequest) => {
    try {
      await createAnnotation(data);
    } catch (error: any) {
      console.error('[PDFViewerModal] Error creating annotation:', error);
      message.error('Failed to create annotation');
    }
  };

  // Handle annotation update
  const handleAnnotationUpdate = async (
    id: string,
    data: { comment?: string; tags?: string[]; color?: string }
  ) => {
    try {
      await updateAnnotation(id, data);
    } catch (error: any) {
      console.error('[PDFViewerModal] Error updating annotation:', error);
      message.error('Failed to update annotation');
    }
  };

  // Handle annotation deletion
  const handleAnnotationDelete = async (id: string) => {
    try {
      await deleteAnnotation(id);
    } catch (error: any) {
      console.error('[PDFViewerModal] Error deleting annotation:', error);
      message.error('Failed to delete annotation');
    }
  };

  // Handle authenticated PDF export with annotations
  const handleExportPdf = useCallback(async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        message.error('You must be logged in to export');
        return;
      }
      const token = await user.getIdToken();
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      message.loading({ content: 'Generating annotated PDF...', key: 'pdf-export', duration: 0 });

      const response = await fetch(`${API_URL}/export-annotated-pdf/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to export PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentTitle || documentId}_annotated.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.success({ content: 'PDF exported successfully', key: 'pdf-export' });
    } catch (error: any) {
      console.error('[PDFViewerModal] Error exporting PDF:', error);
      message.error({ content: error.message || 'Failed to export PDF', key: 'pdf-export' });
    }
  }, [documentId, documentTitle]);

  // Handle authenticated cost estimate generation
  const handleGenerateEstimate = useCallback(async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        message.error('You must be logged in to generate estimates');
        return;
      }
      const token = await user.getIdToken();
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      setEstimateLoading(true);
      message.loading({ content: 'Generating cost estimate... This may take a moment.', key: 'estimate-gen', duration: 0 });

      const response = await fetch(`${API_URL}/generate-estimate/${documentId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate estimate');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentTitle || documentId} - Cost Estimate.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.success({ content: 'Cost estimate downloaded!', key: 'estimate-gen' });
    } catch (error: any) {
      console.error('[PDFViewerModal] Error generating estimate:', error);
      message.error({ content: error.message || 'Failed to generate estimate', key: 'estimate-gen' });
    } finally {
      setEstimateLoading(false);
    }
  }, [documentId, documentTitle]);

  // Streaming chat: owner attaches auth + endpoint, returns the raw Response so
  // ReportAssistant can read the SSE stream.
  const handleAssistantSend = useCallback(async (messages: ChatMessage[]): Promise<Response> => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    return fetch(`${API_URL}/report-chat/${documentId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ messages }),
    });
  }, [documentId]);

  // Handle authenticated work order generation - returns Blob for modal download
  const handleGenerateWorkOrder = useCallback(async (annotationIds: string[]): Promise<Blob | null> => {
    if (!annotationIds.length) return null;
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to generate work orders');
      }
      const token = await user.getIdToken();
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      const response = await fetch(`${API_URL}/generate-work-order/${documentId}`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ annotation_ids: annotationIds }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate work order');
      }

      return await response.blob();
    } catch (error: any) {
      console.error('[PDFViewerModal] Error generating work order:', error);
      throw error;
    }
  }, [documentId]);

  // Clean up state when modal closes
  const handleClose = () => {
    console.log('[PDFViewerModal] Closing modal, cleaning up state');
    setIsLoadingPDF(false);
    onClose();
  };

  // Show loading spinner while annotations are being fetched
  const isLoading = annotationsLoading || isLoadingPDF;

  return (
    <>
      <style>{`
        body.pdf-viewer-open .mobile-menu-trigger { display: none !important; }
      `}</style>
      <Modal
      open={visible}
      onCancel={handleClose}
      width={isMobile ? '100vw' : '95vw'}
      style={
        isMobile
          ? { top: 0, left: 0, margin: 0, paddingBottom: 0, maxWidth: '100vw' }
          : { top: 20, paddingBottom: 0, maxWidth: '95vw' }
      }
      bodyStyle={
        isMobile
          ? { height: '100vh', padding: 0, overflow: 'hidden' }
          : { height: 'calc(100vh - 40px)', padding: 0, overflow: 'hidden' }
      }
      styles={
        isMobile
          ? {
              body: { height: '100vh', padding: 0, overflow: 'hidden' },
              content: { padding: 0, height: '100vh', borderRadius: 0 },
            }
          : {
              body: {
                height: 'calc(100vh - 40px)',
                padding: 0,
                overflow: 'hidden',
                borderRadius: 8,
              },
              content: {
                padding: 0,
                height: 'calc(100vh - 40px)',
                borderRadius: 8,
                overflow: 'hidden',
              },
            }
      }
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      maskClosable={false}
      wrapClassName="pdf-viewer-fullscreen-modal"
    >
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Spin size="large" />
          <Typography.Text type="secondary">
            {annotationsLoading ? 'Loading annotations...' : 'Loading PDF...'}
          </Typography.Text>
        </div>
      ) : (
        <PDFViewer
          pdfUrl={pdfUrl}
          documentId={documentId}
          documentTitle={documentTitle}
          onClose={handleClose}
          annotations={annotations}
          onAnnotationCreate={handleAnnotationCreate}
          onAnnotationUpdate={handleAnnotationUpdate}
          onAnnotationDelete={handleAnnotationDelete}
          onExportPdf={handleExportPdf}
          onGenerateEstimate={handleGenerateEstimate}
          estimateLoading={estimateLoading}
          enableWorkOrder={true}
          onGenerateWorkOrder={handleGenerateWorkOrder}
          enableAssistant={true}
          onAssistantSend={handleAssistantSend}
          sections={sections}
        />
      )}
    </Modal>
    </>
  );
};
